/**
 * delete_tester_note Tool — archive a tester knowledge-base note by ID (Issue #2661)
 *
 * Maps to the API's archive endpoint (soft delete). Named `delete_tester_note`
 * to match the user-facing CLI surface (`runhuman notes delete`); the
 * "archive" vocabulary was retired in PR #3232.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export interface DeleteTesterNoteArgs {
  readonly noteId: string;
}

export const deleteTesterNoteToolDefinition: Tool = {
  name: 'delete_tester_note',
  description: `Delete (archive) a tester knowledge-base note by ID.

This is a soft delete: the note is hidden from list_tester_notes but the row remains for audit. Requires the caller's PAT to be Volter staff or an admin of the note's organization.`,
  inputSchema: {
    type: 'object',
    properties: {
      noteId: {
        type: 'string',
        description: 'Note ID to delete (required).',
      },
    },
    required: ['noteId'],
  },
};

export async function handleDeleteTesterNote(
  args: DeleteTesterNoteArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    await jobClient.archiveTesterNote(args.noteId);

    return {
      content: [{
        type: 'text',
        text: `Tester note deleted.

Note ID: ${args.noteId}

The note is no longer visible to list_tester_notes. It can be re-created with create_tester_note if needed.`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to delete tester note\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
